import type { AttentionReason, OrderFilters } from "../api/use-orders";

import { MARKETPLACE_STATUS_CODES } from "./marketplace-status-labels";

/** Lifecycle statuses shown in the status filter — also the validation whitelist. */
export const LIFECYCLES = [
  "received",
  "reserved",
  "picked",
  "packed",
  "shipped",
  "delivered",
  "cancelled",
  "returned",
  "superseded",
] as const;

/**
 * Lets a key be passed as `undefined` (to clear it) without violating
 * `exactOptionalPropertyTypes`. Used at the filter-change callback boundary.
 */
export type FilterUpdate = {
  [K in keyof OrderFilters]?: OrderFilters[K] | undefined;
};

const STATUSES = new Set<string>(LIFECYCLES);
const PAYMENTS = new Set<string>(["cod", "prepaid"]);
const ATTENTION_VALUES = new Set<string>(["any", "oversold", "buyer_cancel_requested"]);
const AWB = new Set(["present", "missing"]);
const BOOL = new Set(["true", "false"]);
const SHIP_BY = new Set(["overdue", "today", "next_24h"]);

/** Splits the search box / URL value the same way the BE does. */
export function searchTokens(search: string): string[] {
  const seen = new Set<string>();
  return search
    .split(/[,;\s]+/)
    .map((t) => t.trim())
    .filter((t) => t !== "" && !seen.has(t) && (seen.add(t), true));
}

export function joinTokens(tokens: string[]): string {
  return tokens.join(",");
}

/** Keys the Filters drawer owns (quick-bar keys are everything else). */
export const DRAWER_KEYS = [
  "status",
  "awb",
  "sof",
  "gift",
  "ship_by",
  "courier",
  "payment_channel",
  "seller_id",
] as const satisfies readonly (keyof OrderFilters)[];

export function countDrawerFilters(f: OrderFilters): number {
  return DRAWER_KEYS.filter((k) => {
    const v = f[k];
    return Array.isArray(v) ? v.length > 0 : v !== undefined && v !== "";
  }).length;
}

/**
 * Parses TanStack Router's raw search record into a typed OrderFilters.
 * Body of the /orders route's `validateSearch`. Invalid enum values are dropped
 * (degrade to "no filter"); empty values are omitted so the URL stays clean.
 * `marketplace` and `seller_id` are open/dynamic vocabularies, so any non-empty
 * string is accepted.
 */
export function parseOrderSearch(raw: Record<string, unknown>): OrderFilters {
  const out: OrderFilters = {};
  // TanStack Router JSON-parses each query value, so `?search=2788247632069007`
  // arrives as a number and `?exception_only=true` as a boolean. Normalise to
  // string before any `.trim()` / Set lookup; anything exotic degrades to "".
  const str = (v: unknown): string =>
    typeof v === "string" ? v : typeof v === "number" || typeof v === "boolean" ? String(v) : "";

  const marketplace = str(raw.marketplace).trim();
  if (marketplace) out.marketplace = marketplace;

  // `out.status` must always be set explicitly (to the validated array, or to
  // `undefined`) whenever `raw.status` is present — never simply omitted.
  // TanStack Router's matchRoutesInternal merges this function's return value
  // over the raw (unvalidated) search object, so an omitted key falls through
  // from the raw URL. An all-invalid `?status=bogus` would otherwise survive
  // the merge as a raw string typed `string[]`, crashing buildChips's
  // `f.status.map(...)`. Also accept an array (the drawer's Apply flow passes
  // a real `string[]`, and the router may hand that back as-is too).
  if (raw.status !== undefined) {
    const rawStatus = Array.isArray(raw.status) ? raw.status.join(",") : str(raw.status);
    const status = rawStatus
      .split(",")
      .map((s) => s.trim())
      .filter((s) => STATUSES.has(s));
    out.status = status.length > 0 ? status : undefined;
  }

  const payment = str(raw.payment);
  if (payment && PAYMENTS.has(payment)) out.payment = payment;

  const awb = str(raw.awb);
  if (AWB.has(awb)) out.awb = awb as "present" | "missing";

  const sof = str(raw.sof);
  if (BOOL.has(sof)) out.sof = sof as "true" | "false";

  const gift = str(raw.gift);
  if (BOOL.has(gift)) out.gift = gift as "true" | "false";

  const shipBy = str(raw.ship_by);
  if (SHIP_BY.has(shipBy)) out.ship_by = shipBy as "overdue" | "today" | "next_24h";

  const courier = str(raw.courier).trim();
  if (courier) out.courier = courier;

  const paymentChannel = str(raw.payment_channel).trim();
  if (paymentChannel) out.payment_channel = paymentChannel;

  // marketplace_status is only meaningful paired with its own marketplace — a code
  // from one marketplace's list (e.g. tiktok's on_hold) is not valid for another
  // (e.g. shopee). Requiring `marketplace` here and checking it against that
  // marketplace's own code list prevents an active-but-invisible filter state
  // (see OrderFilters.tsx's mkpStatusKey, which needs both fields to render).
  const marketplaceStatus = str(raw.marketplace_status);
  if (
    marketplaceStatus &&
    marketplace &&
    MARKETPLACE_STATUS_CODES[marketplace]?.includes(marketplaceStatus)
  ) {
    out.marketplace_status = marketplaceStatus;
  }

  const sellerId = str(raw.seller_id).trim();
  if (sellerId) out.seller_id = sellerId;

  const search = str(raw.search).trim();
  if (search) out.search = search;

  if (str(raw.exception_only) === "true") out.exception_only = true;

  const attention = str(raw.attention);
  if (attention && ATTENTION_VALUES.has(attention)) {
    out.attention = attention as AttentionReason | "any";
  }

  // Paging — absent key arrives as undefined → Number(undefined) = NaN → dropped.
  // We do NOT use `raw.offset ?? ""` because Number("") = 0 which would coerce a
  // missing key to offset:0. Accessing raw.offset directly keeps missing = undefined.
  const offset = Number(raw.offset);
  if (Number.isInteger(offset) && offset >= 0) out.offset = offset;
  const limit = Number(raw.limit);
  if (Number.isInteger(limit) && limit > 0) out.limit = limit;

  // Date range — validated server-side, but accept only YYYY-MM-DD shape here.
  const df = str(raw.date_from).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(df)) out.date_from = df;
  const dt = str(raw.date_to).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(dt)) out.date_to = dt;

  return out;
}

/** True when at least one filter is active — drives Clear-button visibility.
 * Paging fields (offset/limit) are intentionally excluded; date range counts
 * as an active filter since it represents a user-applied constraint. */
export function hasActiveFilters(f: OrderFilters): boolean {
  return Boolean(
    f.marketplace ||
    f.status?.length ||
    f.payment ||
    f.seller_id ||
    f.marketplace_status ||
    f.search ||
    f.exception_only ||
    f.date_from ||
    f.date_to ||
    f.awb ||
    f.sof ||
    f.gift ||
    f.ship_by ||
    f.courier ||
    f.payment_channel,
  );
}

/** Returns the filter with offset cleared — call when any non-paging filter changes
 * so results jump back to page 1. */
export function resetPage(f: OrderFilters): OrderFilters {
  return { ...f, offset: 0 };
}

/**
 * Produces the next filter from the current one plus a set of updates.
 * A key whose update value is `undefined` is removed (clears that filter).
 */
export function mergeFilter(current: OrderFilters, updates: FilterUpdate): OrderFilters {
  const next: OrderFilters = { ...current };
  for (const k of Object.keys(updates) as (keyof FilterUpdate)[]) {
    if (updates[k] === undefined) {
      delete next[k];
    } else {
      (next as Record<string, unknown>)[k] = updates[k];
    }
  }
  return next;
}
